import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { useCallback, useRef, useState } from "react";

/** How long the downloaded PDF's blob is kept once its download started. */
const BLOB_REVOKE_DELAY_MS = 1_000;

type DownloadableArtifact = {
  readonly artifactId: string;
  readonly title: string;
};

/**
 * Downloads an Artifact of `conversationId` as a PDF file, rendered by the
 * API (Gotenberg) — the panel's Download button.
 *
 * Fetched as a blob through `useApi()` rather than linked: the route needs
 * the Clerk token and the `team-id` header, which a plain link cannot carry.
 * The blob is only ever saved, never rendered here, so it does not matter
 * that its URL lives on the app's origin.
 */
export function useArtifactPdfDownload(conversationId: string): {
  readonly download: (artifact: DownloadableArtifact) => Promise<void>;
  readonly isDownloading: boolean;
} {
  const api = useApi();
  const toastError = useToastError();
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);
  // A ref, not the state: two clicks in the same render would both read
  // `isDownloading` as false and render the PDF twice.
  const isDownloadingRef = useRef(false);

  const download = useCallback(
    async ({ artifactId, title }: DownloadableArtifact): Promise<void> => {
      if (isDownloadingRef.current) {
        return;
      }
      isDownloadingRef.current = true;
      setIsDownloading(true);
      try {
        const { data } = await api.get<Blob>(
          `/conversation/${conversationId}/artifact/${artifactId}/pdf`,
          { responseType: "blob" }
        );
        const url = URL.createObjectURL(data);
        // In the page while clicked, and revoked a moment later: Firefox
        // ignores a click on a detached link, and Firefox and Safari can
        // cancel a download whose blob is revoked in the same tick.
        const link = document.createElement("a");
        link.href = url;
        link.download = `${title}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), BLOB_REVOKE_DELAY_MS);
      } catch {
        toastError({
          title: t("chat.artifact.downloadFailed"),
          description: t("chat.artifact.downloadFailedDescription"),
        });
      } finally {
        isDownloadingRef.current = false;
        setIsDownloading(false);
      }
    },
    [api, conversationId, toastError, t]
  );

  return { download, isDownloading };
}
